import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { articlesAPI } from '../services/api';
import { validateAdminAccess, clearAdminAuth } from '../utils/security';
import './AdminDashboard.css';

function AdminDashboard() {
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const navigate = useNavigate();

  const articlesPerPage = 20;
  const categories = ['all', 'film', 'tv', 'awards', 'festivals', 'reviews'];

  useEffect(() => {
    if (!validateAdminAccess()) {
      navigate('/iffa-admin-login');
      return;
    }

    const fetchArticles = async () => {
      try {
        const response = await articlesAPI.getArticles();
        const allArticles = response.posts || response || [];
        setArticles(allArticles);
        setFilteredArticles(allArticles);
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [navigate]);

  useEffect(() => {
    let filtered = articles;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(article =>
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.author.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(article =>
        article.category && article.category.toLowerCase() === selectedCategory
      );
    }

    // Sort articles
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt || b.publishTime) - new Date(a.createdAt || a.publishTime);
        case 'oldest':
          return new Date(a.createdAt || a.publishTime) - new Date(b.createdAt || b.publishTime);
        case 'title':
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

    setFilteredArticles(filtered);
    setCurrentPage(1);
  }, [articles, searchTerm, selectedCategory, sortBy]);

  const handleDelete = async (articleId, articleTitle) => {
    if (window.confirm(`Are you sure you want to delete "${articleTitle}"? This action cannot be undone.`)) {
      try {
        await articlesAPI.deleteArticle(articleId);
        setArticles(articles.filter(article => article.id !== articleId));
        alert('✅ Article deleted successfully!');
      } catch (error) {
        alert('❌ Error deleting article: ' + error.message);
      }
    }
  };

  const handleLogout = () => {
    clearAdminAuth();
    navigate('/iffa-admin-login');
  };

  if (loading) return <div className="admin-loading">Loading admin dashboard...</div>;

  // Pagination
  const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);
  const startIndex = (currentPage - 1) * articlesPerPage;
  const currentArticles = filteredArticles.slice(startIndex, startIndex + articlesPerPage);

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <div className="admin-title">
          <h1>IFFA News Admin</h1>
          <p>{filteredArticles.length} articles total</p>
        </div>
        <div className="admin-actions">
          <Link to="/iffa-admin-2024/create" className="btn btn-primary">
            + New Article
          </Link>
          <button onClick={handleLogout} className="btn btn-danger">
            Logout
          </button>
        </div>
      </div>

      <div className="admin-filters">
        <div className="filter-row">
          <input
            type="text"
            placeholder="Search articles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="category-select"
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>
                {cat === 'all' ? 'All Categories' : cat.charAt(0).toUpperCase() + cat.slice(1)}
              </option>
            ))}
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="title">Title A-Z</option>
          </select>
        </div>
      </div>

      <div className="articles-grid">
        {currentArticles.map(article => (
          <div key={article.id} className="article-card">
            <div className="article-image">
              <img src={article.image} alt={article.title} />
              <span className="category-badge">{article.category}</span>
            </div>
            <div className="article-info">
              <h3>{article.title}</h3>
              <p className="article-subtitle">{article.subtitle}</p>
              <div className="article-meta">
                <span>{article.author}</span>
                <span>{article.publishTime}</span>
              </div>
              <div className="article-actions">
                <Link to={`/article/${article.id}`} className="btn btn-view">
                  View
                </Link>
                <Link to={`/iffa-admin-2024/edit/${article.id}`} className="btn btn-edit">
                  Edit
                </Link>
                <button 
                  onClick={() => handleDelete(article.id, article.title)}
                  className="btn btn-delete"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="btn btn-secondary"
          >
            Previous
          </button>
          
          <span className="page-info">
            Page {currentPage} of {totalPages}
          </span>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="btn btn-secondary"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;